import { apiClient } from '@leap4/frontend';
import { useCallback, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { reportingBaseUrl } from '../../../../config/generals';

export type QueryStatus = 'running' | 'completed' | 'failed' | 'cancelled';

export interface QueryStatusResponse<TResult = any> {
  queryId: string;
  status: QueryStatus;
  type: string;
  createdAt: string;
  completedAt: string | null;
  elapsedMs: number;
  error: { message: string; code?: string } | null;
  result: TResult | null;
}

interface SubmittedResponse {
  queryId: string;
  status: QueryStatus;
}

interface UseAsyncQueryReturn<TResult> {
  submit: (body: Record<string, any>, path?: string) => Promise<string>;
  queryId: string | null;
  status: QueryStatus | null;
  data: TResult | null;
  error: { message: string; code?: string } | null;
  elapsedMs: number;
  isRunning: boolean;
  isCompleted: boolean;
  cancel: () => Promise<void>;
  reset: () => void;
}

const POLL_INTERVAL_MS = 2000;

export function useAsyncQuery<TResult = any>(): UseAsyncQueryReturn<TResult> {
  const [queryId, setQueryId] = useState<string | null>(null);
  const prevQueryIdRef = useRef<string | null>(null);
  const submitSeqRef = useRef(0);

  const { data: statusData } = useQuery<QueryStatusResponse<TResult>>(
    ['query-status', queryId],
    async () => {
      const response = await apiClient.get<QueryStatusResponse<TResult>>(
        `${reportingBaseUrl}/queries/${queryId}`,
      );
      return response.data;
    },
    {
      enabled: !!queryId,
      refetchInterval: (data) => {
        if (!data) return POLL_INTERVAL_MS;
        return data.status === 'running' ? POLL_INTERVAL_MS : false;
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: 1,
    },
  );

  const cancelById = useCallback(async (id: string) => {
    try {
      await apiClient.delete(`${reportingBaseUrl}/queries/${id}`);
    } catch {

    }
  }, []);

  /**
   * Submits, and guarantees at most one query is left running.
   *
   * The sequence number is claimed synchronously, so submits fired in the same tick see
   * each other. Cancelling on `prevQueryIdRef` alone cannot: that ref is only assigned
   * once the POST resolves, so same-tick submits all read the same stale value, none
   * cancels any other, and every one of them runs to completion on its own backend.
   */
  const submit = useCallback(async (body: Record<string, any>, path = '/queries'): Promise<string> => {
    const seq = ++submitSeqRef.current;

    const superseded = prevQueryIdRef.current;
    if (superseded) {
      prevQueryIdRef.current = null;
      cancelById(superseded).catch(() => { });
    }

    const response = await apiClient.post<SubmittedResponse>(
      `${reportingBaseUrl}${path}`,
      body,
    );

    const newId = response.data.queryId;

    // A newer submit started while this POST was in flight. Nothing will read this
    // result, so kill its backend rather than let it run the query out.
    if (seq !== submitSeqRef.current) {
      cancelById(newId).catch(() => { });
      return newId;
    }

    prevQueryIdRef.current = newId;
    setQueryId(newId);
    return newId;
  }, [cancelById]);

  const cancel = useCallback(async () => {
    if (queryId) {
      await cancelById(queryId);
      prevQueryIdRef.current = null;
      setQueryId(null);
    }
  }, [queryId, cancelById]);

  const reset = useCallback(() => {
    prevQueryIdRef.current = null;
    setQueryId(null);
  }, []);

  const status = statusData?.status ?? (queryId ? 'running' : null);
  const isRunning = status === 'running';
  const isCompleted = status === 'completed';

  return {
    submit,
    queryId,
    status,
    data: statusData?.result ?? null,
    error: statusData?.error ?? null,
    elapsedMs: statusData?.elapsedMs ?? 0,
    isRunning,
    isCompleted,
    cancel,
    reset,
  };
}
